"use client";
import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/authContext";
import { Sparkles, Star, UserCheck, UserPlus, Clock } from "lucide-react";
import { useMutation, useQuery } from "@apollo/client";
import {
  ADD_ASSOCIATION_MUTATION,
  AssociationResponse,
  CHECK_FOLLOW,
} from "@/api/apollo-client/associationQueries";
import { toast } from "react-hot-toast";
import { profile } from "console";

export enum AssociationState {
  TRUE = "TRUE",
  FALSE = "FALSE",
  PENDING = "PENDING",
}

export enum ButtonVariant {
  ICON_ONLY = "icon_only",
  ICON_WITH_TEXT = "icon_with_text",
  TEXT_ONLY = "text_only",
}
export enum Association {
  FOLLOW = "follow",
  PATRON = "patron",
}

interface FollowButtonProps {
  _user: {
    id: string;
    name: string;
    profile_picture?: { url: string };
  };
  initialFollowStatus?: AssociationState;
  initialPatronStatus?: AssociationState;
  variant?: ButtonVariant;
  className?: string;
  clips?: boolean; // New prop for clips mode
  buttonOrientation?: "row" | "column";
}

const FollowButton = ({
  _user,
  initialFollowStatus,
  initialPatronStatus,
  variant = ButtonVariant.ICON_WITH_TEXT,
  className,
  clips = false, // Default false for backward compatibility
  buttonOrientation = "row",
}: FollowButtonProps) => {
  const tempFollowStatus = initialFollowStatus || AssociationState.FALSE;
  const tempPatronStatus = initialPatronStatus || AssociationState.FALSE;
  const [follow, setFollow] = useState(tempFollowStatus);
  const [patron, setPatron] = useState(tempPatronStatus);
  const [showWarning, setShowWarning] = useState(false);
  const [actionType, setActionType] = useState<
    | "unfollow"
    | "unpatron"
    | "cancel_pending_follow"
    | "cancel_pending_patron"
    | null
  >(null);

  const { user } = useAuth();
  const [addAssociation] = useMutation(ADD_ASSOCIATION_MUTATION);
  if (initialFollowStatus == undefined || initialPatronStatus == undefined) {
    const { data: followData, refetch: refetchFollowStatus } = useQuery(
      CHECK_FOLLOW,
      {
        variables: { userId: _user.id },
        skip: !user?.id || !_user?.id,
      }
    );

    useEffect(() => {
      if (followData?.checkFollowerOrPatron) {
        const {
          follower,
          follow_pending,
          patron: patronStatus,
          patron_pending,
        } = followData.checkFollowerOrPatron;

        setFollow(
          follow_pending
            ? AssociationState.PENDING
            : follower
              ? AssociationState.TRUE
              : AssociationState.FALSE
        );
        if (patron_pending) {
          setFollow(AssociationState.PENDING);
        }
        setPatron(
          patron_pending
            ? AssociationState.PENDING
            : patronStatus
              ? AssociationState.TRUE
              : AssociationState.FALSE
        );
      }
    }, [followData]);
  }

  async function handleFollow(confirmAction?: boolean) {
    if (!user?.id || !_user?.id) return;
    toast.dismiss();

    if (follow === AssociationState.PENDING && !confirmAction) {
      setActionType("cancel_pending_follow");
      setShowWarning(true);
      return;
    }

    if (follow === AssociationState.TRUE && !confirmAction) {
      setActionType("unfollow");
      setShowWarning(true);
      return;
    }

    try {
      const { data, errors } = await addAssociation({
        variables: {
          current_user: user.id,
          user_being_associated: _user.id,
          association: Association.FOLLOW,
        },
      });

      if (errors && errors.length > 0) {
        const error = errors[0];
        if (error.message.includes("409")) {
          toast.error("You already have a pending follow request");
        } else {
          toast.error("Failed to follow user");
          console.error("Error in follow/unfollow:", error);
        }
        return;
      }

      if (!data?.addAssociation) {
        toast.error("Failed to follow user");
        return;
      }

      const response = data.addAssociation.response;
      if (response === AssociationResponse.ACCEPTED) {
        toast.success("Successfully followed user");
        setFollow(AssociationState.TRUE);
      } else if (response === AssociationResponse.REQUEST_SENT) {
        toast.success("Follow request sent");
        setFollow(AssociationState.PENDING);
      } else if (response === AssociationResponse.REMOVED) {
        toast.success("Unfollowed user");
        setFollow(AssociationState.FALSE);
      }
    } catch (error) {
      console.error("Error in follow/unfollow:", error);
      toast.error("Failed to follow user");
    }
  }

  async function handlePatron(confirmAction?: boolean) {
    if (!user?.id || !_user?.id) return;
    toast.dismiss();

    if (patron === AssociationState.PENDING && !confirmAction) {
      setActionType("cancel_pending_patron");
      setShowWarning(true);
      return;
    }

    if (patron === AssociationState.TRUE && !confirmAction) {
      setActionType("unpatron");
      setShowWarning(true);
      return;
    }

    try {
      const { data, errors } = await addAssociation({
        variables: {
          current_user: user.id,
          user_being_associated: _user.id,
          association: Association.PATRON,
        },
      });

      if (errors && errors.length > 0) {
        const error = errors[0];
        if (error.message.includes("409")) {
          toast.error("You already have a pending patron request");
        } else {
          toast.error("Failed to become patron");
          console.error("Error in patron/unpatron:", error);
        }
        return;
      }

      if (!data?.addAssociation) {
        toast.error("Failed to become patron");
        return;
      }

      const response = data.addAssociation.response;
      if (response === AssociationResponse.ACCEPTED) {
        toast.success("Successfully became patron");
        setPatron(AssociationState.TRUE);
        setFollow(AssociationState.TRUE);
      } else if (response === AssociationResponse.REQUEST_SENT) {
        toast.success("Patron request sent");
        setPatron(AssociationState.PENDING);
      } else if (response === AssociationResponse.REMOVED) {
        toast.success("Removed patron status");
        setPatron(AssociationState.FALSE);
      }
    } catch (error) {
      console.error("Error in patron/unpatron:", error);
      toast.error("Failed to become patron");
    }
  }

  if (!user || !_user) return null;
  if (user.id === _user.id) return null;

  const getButtonClasses = (isPending: boolean, isActive: boolean) => {
    // Clips mode styling - simple follow button
    if (clips) {
      const baseClasses = `flex flex-row items-center justify-center rounded-full text-sm font-semibold transition-all duration-200 px-3 py-1.5`;

      if (isPending) {
        return `${baseClasses} bg-amber-500 text-white hover:bg-amber-600 cursor-pointer`;
      }
      if (isActive) {
        return `${baseClasses} bg-blue-500 text-white hover:bg-blue-600`;
      }
      return `${baseClasses} bg-white/20 text-white hover:bg-white/30 border border-white/30`;
    }

    // Default styling
    const baseClasses = `flex flex-row items-center justify-center rounded-full text-sm font-semibold transition-all duration-200 ${
      variant === ButtonVariant.ICON_ONLY
        ? "px-3 py-1.5"
        : variant === ButtonVariant.TEXT_ONLY
          ? "lg:px-4 px-2 py-2 min-w-max"
          : "lg:px-4 lg:py-2 md:px-3.5 px-2 py-2 min-w-max"
    }`;

    if (isPending) {
      return `${baseClasses} bg-amber-500 text-white hover:bg-amber-600 cursor-pointer`;
    }
    if (isActive) {
      return `${baseClasses} bg-blue-500 text-white hover:bg-blue-600`;
    }
    return `${baseClasses} bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600`;
  };

  const getPatronButtonClasses = (isPending: boolean, isActive: boolean) => {
    const baseClasses = `flex flex-row items-center justify-center rounded-full text-sm font-semibold transition-all duration-200 ${
      variant === ButtonVariant.ICON_ONLY
        ? "px-3 py-1.5"
        : variant === ButtonVariant.TEXT_ONLY
          ? "lg:px-4 px-2 py-2 min-w-max"
          : "lg:px-4  py-2 px-2 w-full md:w-auto"
    }`;

    if (isPending) {
      return `${baseClasses} bg-amber-500 text-white hover:bg-amber-600 cursor-pointer`;
    }
    if (isActive) {
      return `${baseClasses} bg-gradient-to-r from-[#e0aa3e] to-[#f9f295] text-gray-900 hover:from-[#d4a03a] hover:to-[#e8e08a]`;
    }
    return `${baseClasses} bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-300`;
  };

  return (
    <>
      <div
        className={`flex ${
          buttonOrientation === "column"
            ? "flex-col items-stretch"
            : "items-center"
        } lg:gap-3 gap-2 ${className || ""}`}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        {patron === AssociationState.FALSE && (
          <button
            onClick={(e) => {
              e.preventDefault();
              handleFollow();
            }}
            className={getButtonClasses(
              follow === AssociationState.PENDING,
              follow === AssociationState.TRUE
            )}
          >
            <div className="flex items-center justify-center space-x-1.5 ">
              {/* Always show icon in clips mode, or based on variant for non-clips */}
              {(clips || variant !== ButtonVariant.TEXT_ONLY) &&
                (follow === AssociationState.PENDING ? (
                  <Clock className="lg:w-3.5 lg:h-3.5 w-4 h-4 md:hidden" />
                ) : follow === AssociationState.TRUE ? (
                  <UserCheck className="lg:w-3.5 lg:h-3.5 w-4 h-4 " />
                ) : (
                  <UserPlus className="lg:w-3.5 lg:h-3.5 w-4 h-4 " />
                ))}
              {/* Show text for all variants except ICON_ONLY.
                 For ICON_WITH_TEXT hide on mobile and show on md+ */}
              {variant !== ButtonVariant.ICON_ONLY && (
                <span
                  className={`${
                    variant === ButtonVariant.ICON_WITH_TEXT
                      ? "hidden md:inline md:w-auto md:h-auto md:text-xs"
                      : ""
                  } whitespace-nowrap`}
                >
                  {follow === AssociationState.PENDING
                    ? "Pending"
                    : follow === AssociationState.TRUE
                      ? "Following"
                      : " Follow "}
                </span>
              )}
            </div>
          </button>
        )}

        {!clips && (
          <button
            onClick={(e) => {
              e.preventDefault();
              handlePatron();
            }}
            className={getPatronButtonClasses(
              patron === AssociationState.PENDING,
              patron === AssociationState.TRUE
            )}
          >
            <div className="flex items-center justify-center  space-x-1.5 ">
              {variant !== ButtonVariant.TEXT_ONLY &&
                (patron === AssociationState.PENDING ? (
                  <Clock className="lg:w-3.5 lg:h-3.5 w-4 h-4 md:hidden" />
                ) : patron === AssociationState.TRUE ? (
                  <Sparkles className="lg:w-3.5 lg:h-3.5 w-4 h-4" />
                ) : (
                  <Star className="lg:w-3.5 lg:h-3.5 w-4 h-4" />
                ))}
              {variant === ButtonVariant.ICON_WITH_TEXT && (
                <span className="hidden md:inline whitespace-nowrap md:w-auto md:h-auto md:text-xs">
                  {patron === AssociationState.PENDING
                    ? "Pending"
                    : patron === AssociationState.TRUE
                      ? "Patron"
                      : " Patron "}
                </span>
              )}
              {variant === ButtonVariant.TEXT_ONLY && (
                <span className="whitespace-nowrap">
                  {patron === AssociationState.PENDING
                    ? "Pending"
                    : patron === AssociationState.TRUE
                      ? "Patron"
                      : "Patron"}
                </span>
              )}
            </div>
          </button>
        )}
      </div>

      {showWarning && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className="w-full max-w-md bg-white dark:bg-gray-900 rounded-lg shadow-lg p-5 sm:p-6">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-3 text-center">
              Are you sure?
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-5 text-center">
              {actionType === "unfollow"
                ? "Do you really want to unfollow this user?"
                : actionType === "unpatron"
                  ? "Do you really want to stop being a patron?"
                  : actionType === "cancel_pending_follow"
                    ? "Do you want to cancel your pending follow request?"
                    : actionType === "cancel_pending_patron"
                      ? "Do you want to cancel your pending patron request?"
                      : ""}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
              <button
                onClick={() => setShowWarning(false)}
                className="w-full sm:w-auto px-4 py-2 text-sm bg-gray-200 dark:bg-gray-700 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (
                    actionType === "unfollow" ||
                    actionType === "cancel_pending_follow"
                  ) {
                    handleFollow(true);
                  } else if (
                    actionType === "unpatron" ||
                    actionType === "cancel_pending_patron"
                  ) {
                    handlePatron(true);
                  }
                  setShowWarning(false);
                  setActionType(null);
                }}
                className="w-full sm:w-auto px-4 py-2 text-sm bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                {actionType === "unfollow"
                  ? "Unfollow"
                  : actionType === "unpatron"
                    ? "Remove Patron"
                    : actionType === "cancel_pending_follow"
                      ? "Cancel Follow Request"
                      : actionType === "cancel_pending_patron"
                        ? "Cancel Patron Request"
                        : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FollowButton;
